import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AnalyticsTabs, AnalyticsTab } from '../components/analytics/AnalyticsTabs';
import { ResourcesSidebar } from '../components/analytics/ResourcesSidebar';
import { EmptyState } from '../components/analytics/EmptyState';
import { AcceptanceTab } from '../components/analytics/AcceptanceTab';
import { AuthenticationTab } from '../components/analytics/AuthenticationTab';
import { DisputesTab } from '../components/analytics/DisputesTab';
import { FeedbackModal } from '../components/analytics/FeedbackModal';
import { Granularity } from '../components/dashboard/GranularitySelector';
interface DateRange {
  start: Date;
  end: Date;
}
export function PaymentAnalytics() {
  const navigate = useNavigate();
  const location = useLocation();
  // Get initial tab from URL query params
  const getInitialTab = (): AnalyticsTab => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as AnalyticsTab;
    return tab || 'acceptance';
  };
  const [activeTab, setActiveTab] = useState<AnalyticsTab>(getInitialTab());
  const [dateRange, setDateRange] = useState<DateRange>({
    start: new Date(new Date().setDate(new Date().getDate() - 90)),
    end: new Date()
  });
  const [granularity, setGranularity] = useState<Granularity>('weekly');
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  // Update URL when tab changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set('tab', activeTab);
    navigate(`${location.pathname}?${params.toString()}`, {
      replace: true
    });
  }, [activeTab]);
  // Update tab when URL changes (browser back/forward)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab') as AnalyticsTab;
    if (tab && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [location.search]);
  const handleTabChange = (tab: AnalyticsTab) => {
    setActiveTab(tab);
  };
  // Get resources based on active tab
  const getResources = () => {
    switch (activeTab) {
      case 'acceptance':
        return [{
          title: 'Acceptance analytics',
          description: 'Learn more about analyzing your card payment acceptance.'
        }];
      case 'authentication':
        return [{
          title: 'Authentication analytics',
          description: 'Learn more about analyzing your payments authentication.'
        }];
      case 'disputes':
        return [{
          title: 'Disputes analytics',
          description: 'Learn more about analyzing your card payment disputes.'
        }, {
          title: 'How disputes work',
          description: 'Learn more about the lifecycle of card disputes.'
        }, {
          title: 'Dispute reasons',
          description: 'Learn more about the reasons for card disputes.'
        }];
      default:
        return [];
    }
  };
  const otherReports = [{
    title: 'View all reports',
    link: '#'
  }];
  // Render content based on active tab
  const renderTabContent = () => {
    const contentVariants = {
      initial: {
        opacity: 0,
        y: 10
      },
      animate: {
        opacity: 1,
        y: 0
      },
      exit: {
        opacity: 0,
        y: -10
      }
    };
    return <AnimatePresence mode="wait" data-id="element-7597">
        <motion.div key={activeTab} variants={contentVariants} initial="initial" animate="animate" exit="exit" transition={{
        duration: 0.2
      }} data-id="element-7598">
          {activeTab === 'acceptance' ? <AcceptanceTab dateRange={dateRange} granularity={granularity} onDateRangeChange={setDateRange} onGranularityChange={setGranularity} data-id="element-7599" /> : activeTab === 'authentication' ? <AuthenticationTab dateRange={dateRange} granularity={granularity} onDateRangeChange={setDateRange} onGranularityChange={setGranularity} data-id="element-7600" /> : activeTab === 'disputes' ? <DisputesTab dateRange={dateRange} granularity={granularity} onDateRangeChange={setDateRange} onGranularityChange={setGranularity} data-id="element-7601" /> : <div className="bg-white rounded-lg border border-gray-200 p-8" data-id="element-7602">
              <EmptyState message={`${activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} analytics content coming soon`} submessage="This section will display detailed analytics charts and tables." data-id="element-7603" />
            </div>}
        </motion.div>
      </AnimatePresence>;
  };
  return <div data-id="element-7604">
      {/* Page Header */}
      <div className="mb-6" data-id="element-7605">
        <div className="flex items-center justify-between mb-4" data-id="element-7606">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-7607">
            Payments analytics
          </h1>
          <button onClick={() => setShowFeedbackModal(true)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-7608">
            Share feedback
          </button>
        </div>

        {/* Tabs */}
        <AnalyticsTabs activeTab={activeTab} onChange={handleTabChange} data-id="element-7609" />
      </div>

      {/* Main Content with Sidebar */}
      <div className="flex gap-6" data-id="element-7610">
        {/* Main Content Area */}
        <div className="flex-1 min-w-0" data-id="element-7611">{renderTabContent()}</div>

        {/* Resources Sidebar */}
        <div className="w-80 flex-shrink-0" data-id="element-7612">
          <ResourcesSidebar resources={getResources()} otherReports={otherReports} data-id="element-7613" />
        </div>
      </div>

      {/* Feedback Modal */}
      <FeedbackModal isOpen={showFeedbackModal} onClose={() => setShowFeedbackModal(false)} data-id="element-7614" />
    </div>;
}